import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import apiService from '../../services/apiService';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalJobs: 0,
    totalBlogs: 0,
    totalUsers: 0,
    totalApplications: 0
  });

  useEffect(() => {
    // Simulate fetching stats
    setStats({
      totalJobs: 6,
      totalBlogs: 12,
      totalUsers: 45,
      totalApplications: 23
    });
  }, []);

  const handleLogout = () => {
    apiService.removeAuthToken();
    window.location.href = '/login';
  };

  const statCards = [
    {
      title: 'Total Jobs',
      value: stats.totalJobs,
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
      ),
      color: 'bg-blue-500',
      link: '/admin/jobs'
    },
    {
      title: 'Total Blogs',
      value: stats.totalBlogs,
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
        </svg>
      ),
      color: 'bg-green-500',
      link: '/admin/blogs'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-4.7 6.3c-.33.44-.49.95-.49 1.47V20c0 1.1.9 2 2 2h6c1.1 0 2-.9 2-2z"/>
        </svg>
      ),
      color: 'bg-purple-500',
      link: '/admin/users'
    },
    {
      title: 'Applications',
      value: stats.totalApplications,
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
        </svg>
      ),
      color: 'bg-orange-500',
      link: '/admin/applications'
    }
  ];

  const quickActions = [
    {
      title: 'Add New Job',
      description: 'Create a new job posting',
      icon: '➕',
      link: '/admin/jobs/new'
    },
    {
      title: 'Write Blog Post',
      description: 'Create a new blog article',
      icon: '📝',
      link: '/admin/blogs/new'
    },
    {
      title: 'View Applications',
      description: 'Review job applications',
      icon: '📋',
      link: '/admin/applications'
    },
    {
      title: 'User Management',
      description: 'Manage user accounts',
      icon: '👥',
      link: '/admin/users'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Admin Dashboard - Hospital Build Pvt Ltd</title>
        <meta name="description" content="Admin dashboard for managing Hospital Build Pvt Ltd website content." />
      </Helmet>

      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Welcome back, Admin!</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Last login: Today</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-lg shadow p-6"
            >
              <Link to={card.link} className="block hover:bg-gray-50 rounded-lg p-2 -m-2">
                <div className="flex items-center">
                  <div className={`${card.color} text-white p-3 rounded-lg`}>
                    {card.icon}
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{card.title}</p>
                    <p className="text-2xl font-semibold text-gray-900">{card.value}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (index + 4) * 0.1 }}
              >
                <Link
                  to={action.link}
                  className="block p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="text-2xl mb-2">{action.icon}</div>
                  <h3 className="font-medium text-gray-900">{action.title}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Jobs</h2>
            <div className="space-y-3">
              {[
                { title: 'Assistant Manager - Marketing', status: 'Active', date: '2 days ago' },
                { title: 'Senior Medical Gas Pipeline Engineer', status: 'Active', date: '1 week ago' },
                { title: 'Sales Manager - Healthcare Solutions', status: 'Active', date: '1 week ago' }
              ].map((job, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{job.title}</p>
                    <p className="text-sm text-gray-500">{job.date}</p>
                  </div>
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                    {job.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Applications</h2>
            <div className="space-y-3">
              {[
                { name: 'John Doe', position: 'Assistant Manager - Marketing', date: '1 hour ago' },
                { name: 'Jane Smith', position: 'Senior Engineer', date: '3 hours ago' },
                { name: 'Mike Johnson', position: 'Sales Manager', date: '1 day ago' }
              ].map((application, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">{application.name}</p>
                    <p className="text-sm text-gray-500">{application.position}</p>
                  </div>
                  <span className="text-sm text-gray-500">{application.date}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
